import { styled } from '@mui/material/styles';
import {
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Collapse,
  CircularProgress,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import {
  faPenToSquare,
  faAngleDown,
  faAngleUp,
  faPlus,
  faXmark,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MedicationListItem } from '../../../../../store/hubOwner/types';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchMedicationStatusHistory,
  resetUpdateMedicationStatusSuccess,
  updateMedicationStatus,
} from '../../../../../store/medications/actionCreators';
import CustomerCareSelect from '../../../../Shared/CustomerCareSelect';
import MenuItem from '@mui/material/MenuItem';
import {
  DRUG_USAGE_STATUS,
  parseDateToLocal,
} from '../../../../../shared/utils';
import { fetchMedicationList } from '../../../../../store/hubOwner/actionCreators';
import {
  backgroundInactive,
  dimGrey,
  lightBlack,
  lightGrey,
} from '../../../../../mainTheme';

interface StatusDetailsProps {
  rowData: MedicationListItem | null;
  patientId: number;
}

const DetailLabel = styled(Typography)({
  color: lightBlack,
  fontSize: '16px',
  fontWeight: 700,
  lineHeight: '24px',
  letterSpacing: '0.2px',
  marginBottom: '4px',
});

const statusOptions = [
  { label: 'Active', value: DRUG_USAGE_STATUS.ACTIVE },
  { label: 'Inactive', value: DRUG_USAGE_STATUS.INACTIVE },
  { label: 'Discontinued', value: DRUG_USAGE_STATUS.DISCONTINUED },
];

const reasonOptions = [
  {
    label: "Doesn't understand the directions",
    value: "Doesn't understand the directions",
  },
  { label: 'Too many medications', value: 'Too many medications' },
  {
    label: "Doesn't like the side effects",
    value: "Doesn't like the side effects",
  },
  {
    label: "The medication isn't working",
    value: "The medication isn't working",
  },
  {
    label: 'Doctor advised them not to take',
    value: 'Doctor advised them not to take',
  },
  { label: 'Other', value: 'Other' },
];

const tableCellSx = {
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '24px',
  letterSpacing: '0.2px',
  border: 'none',
  color: lightBlack,
};
const tableHeaderCellSx = {
  fontSize: '18px',
  fontWeight: 500,
  lineHeight: '24px',
  background: backgroundInactive,
  border: 'none',
  color: lightBlack,
};
const statusCellBoxSx = {
  display: 'flex',
  alignItems: 'center',
  height: '100%',
};
const reasonDetailBoxSx = {
  color: dimGrey,
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: '16px',
  wordBreak: 'break-word',
  whiteSpace: 'pre-line',
  maxHeight: '64px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 4,
  WebkitBoxOrient: 'vertical',
  mt: 1,
};

const MedicationStatusDetails: React.FC<StatusDetailsProps> = ({
  rowData,
  patientId,
}) => {
  const dispatch = useDispatch();
  const ndc = rowData?.national_drug_code;
  const { medicationStatusHistory, isStatusHistoryLoading, updateSuccess } =
    useSelector((state: any) => state.medications);

  const [editMode, setEditMode] = useState(false);
  const [status, setStatus] = useState(
    rowData?.status
      ? DRUG_USAGE_STATUS[
          rowData.status.toUpperCase() as keyof typeof DRUG_USAGE_STATUS
        ]
      : DRUG_USAGE_STATUS.ACTIVE,
  );
  const [reason, setReason] = useState('Other');
  const [otherReason, setOtherReason] = useState('');
  const [accordionOpen, setAccordionOpen] = useState(false);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  useEffect(() => {
    if (patientId && ndc) {
      dispatch(fetchMedicationStatusHistory(patientId, ndc));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, ndc]);

  const resetEditFields = () => {
    setStatus(
      rowData?.status
        ? DRUG_USAGE_STATUS[
            rowData.status.toUpperCase() as keyof typeof DRUG_USAGE_STATUS
          ]
        : DRUG_USAGE_STATUS.ACTIVE,
    );
    setReason('Other');
    setOtherReason('');
  };

  const handleEditClick = () => {
    resetEditFields();
    setEditMode(true);
  };

  const handleCancel = () => {
    setEditMode(false);
    resetEditFields();
  };

  const handleSubmit = () => {
    if (!patientId || !ndc) return;
    let change_reason = reason;
    if (reason === 'Other' && otherReason) {
      change_reason = `Other: ${otherReason}`;
    }
    dispatch(updateMedicationStatus(patientId, ndc, status, change_reason));
  };

  useEffect(() => {
    if (updateSuccess && patientId) {
      dispatch(fetchMedicationList(patientId));
      dispatch(resetUpdateMedicationStatusSuccess());
      setEditMode(false);
      resetEditFields();
    }
  }, [updateSuccess, patientId, dispatch]);

  const renderDefaultView = () => {
    return (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <DetailLabel>Status</DetailLabel>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                sx={{ fontWeight: 500, fontSize: '16px', color: lightBlack }}
              >
                {rowData?.status || 'N/A'}
              </Typography>
              <Button
                variant="text"
                color="secondary"
                size="small"
                endIcon={
                  <FontAwesomeIcon
                    icon={faPenToSquare}
                    style={{ fontSize: 16 }}
                  />
                }
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '15px',
                  minWidth: 0,
                  pl: 1,
                  pr: 1,
                }}
                onClick={handleEditClick}
              >
                Change
              </Button>
            </Box>
          </Box>
        </Box>
        {renderStatusHistoryAccordion()}
      </>
    );
  };

  const renderEditView = () => (
    <>
      <Box sx={{ mb: 2 }}>
        <DetailLabel>Status</DetailLabel>
        <CustomerCareSelect
          value={status}
          onChange={(e) => setStatus(Number(e.target.value) as typeof status)}
          sx={{
            width: { xs: '100%', sm: '320px' },
          }}
        >
          {statusOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </CustomerCareSelect>
      </Box>
      <Box sx={{ mb: 2 }}>
        <DetailLabel>Reason</DetailLabel>
        <CustomerCareSelect
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          sx={{
            width: { xs: '100%', sm: '320px' },
          }}
        >
          {reasonOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </CustomerCareSelect>
      </Box>
      {reason === 'Other' && (
        <Box sx={{ mb: 2 }}>
          <DetailLabel sx={{ fontSize: '14px', fontWeight: 500 }}>
            What is the other reason?
          </DetailLabel>
          <TextField
            value={otherReason}
            onChange={(e) => setOtherReason(e.target.value)}
            fullWidth
            multiline
            minRows={4}
            sx={{
              overflow: 'hidden',
              '& .MuiInputBase-root': {
                borderRadius: '8px',
                width: { xs: '100%', sm: '320px' },
                maxHeight: '112px',
                minHeight: '112px',
                overflow: 'auto',
                '& textarea': {
                  '&::placeholder': {
                    color: lightGrey,
                  },
                  overflow: 'auto',
                  maxHeight: '55px',
                },
                '&::before': {
                  display: 'none',
                },
                '&::after': {
                  display: 'none',
                },
              },
              '& .MuiInputBase-input': {
                padding: '12px',
                color: lightBlack,
                overflow: 'auto',
              },
            }}
          />
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSubmit}
          sx={{ minWidth: 100 }}
        >
          Submit
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleCancel}
          sx={{ minWidth: 100 }}
        >
          Cancel
        </Button>
      </Box>
      {renderStatusHistoryAccordion()}
    </>
  );

  function parseReason(row: any) {
    // Handles "Other: details" and other formats since B.E keeps the main and detailed text on one key
    if (row.change_reason && row.change_reason.startsWith('Other:')) {
      const parts = row.change_reason.split(':');
      return { main: 'Other', detail: parts.slice(1).join(':').trim() };
    }
    return { main: row.change_reason, detail: '' };
  }

  function renderStatusHistoryAccordion() {
    return (
      <Accordion
        expanded={accordionOpen}
        onChange={() => setAccordionOpen((prev) => !prev)}
        sx={{ boxShadow: 'none', border: 'none', background: 'none', mb: 1 }}
      >
        <AccordionSummary sx={{ px: 0, minHeight: 40 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '16px',
              color: lightBlack,
              flex: 1,
            }}
          >
            Status history
          </Typography>
          <Typography
            sx={{
              color: '#8A5CC7',
              fontWeight: 500,
              fontSize: '15px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <FontAwesomeIcon
              icon={accordionOpen ? faAngleUp : faAngleDown}
              style={{ fontSize: 18 }}
            />
            {accordionOpen ? 'Collapse' : 'Expand'}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pt: 0 }}>
          {!medicationStatusHistory || medicationStatusHistory.length === 0 ? (
            <Typography sx={{ p: 2, color: '#888', textAlign: 'center' }}>
              No data
            </Typography>
          ) : (
            <TableContainer
              sx={{
                border: '1px solid #CBCBCB',
                borderRadius: '10px',
                boxShadow: 'none',
                background: '#fff',
                overflow: 'hidden',
                mt: 1,
                mb: 1,
                p: 0,
              }}
            >
              <Table
                size="small"
                sx={{
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                  background: '#fff',
                }}
              >
                <TableHead>
                  <TableRow sx={{ background: backgroundInactive }}>
                    <TableCell sx={tableHeaderCellSx}>Status</TableCell>
                    <TableCell sx={tableHeaderCellSx}>Editor</TableCell>
                    <TableCell sx={tableHeaderCellSx}>Reason</TableCell>
                    <TableCell sx={tableHeaderCellSx}>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {medicationStatusHistory.map((row: any, idx: number) => {
                    const reason = parseReason(row);
                    return (
                      <React.Fragment key={idx}>
                        <TableRow
                          sx={{
                            background: '#fff',
                            border: 'none',
                            boxShadow: 'none',
                            '&:last-child td, &:last-child th': { border: 0 },
                          }}
                        >
                          <TableCell sx={tableCellSx}>
                            <Box sx={statusCellBoxSx}>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setExpandedRow(
                                    expandedRow === idx ? null : idx,
                                  )
                                }
                                sx={{ verticalAlign: 'middle', mr: '4px' }}
                              >
                                <FontAwesomeIcon
                                  icon={expandedRow === idx ? faXmark : faPlus}
                                  style={{ fontSize: 14 }}
                                />
                              </IconButton>
                              <span
                                style={{
                                  fontSize: '16px',
                                  fontWeight: 500,
                                  lineHeight: '24px',
                                  color: lightBlack,
                                }}
                              >
                                {row.status}
                              </span>
                            </Box>
                          </TableCell>
                          <TableCell sx={tableCellSx}>
                            {row.updated_by}
                          </TableCell>
                          <TableCell
                            sx={{ ...tableCellSx, verticalAlign: 'top' }}
                          >
                            {reason.main}
                            <Collapse
                              in={expandedRow === idx && !!reason.detail}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box sx={reasonDetailBoxSx} title={reason.detail}>
                                {reason.detail}
                              </Box>
                            </Collapse>
                          </TableCell>
                          <TableCell sx={tableCellSx}>
                            {parseDateToLocal(
                              row.updated_at,
                              'MM/dd/yyyy',
                            )}{' '}
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </AccordionDetails>
      </Accordion>
    );
  }

  if (isStatusHistoryLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={200}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2 }}>
      {editMode ? renderEditView() : renderDefaultView()}
    </Box>
  );
};

export default MedicationStatusDetails;
